import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from "react";
import BlockUi from "react-block-ui";
import "react-block-ui/style.css";
import "loaders.css/loaders.min.css";
import { Loader } from "react-loaders";

import Api from "../utils/Api";

import { AppContext } from "../AppContext";
import { useNormalization } from "../NormalizationContext";
import DesktopMonthNavigator from "../balance/desktop/MonthNavigator/MonthNavigator";
import MobileMonthNavigator from "../balance/mobile/monthNavigator/MonthNavigator";
import SummaryBar from "./components/SummaryBar";
import AnomalyPanel from "./components/AnomalyPanel";
import RecurringExpenses from "./components/RecurringExpenses";
import CategoryBreakdown from "./components/CategoryBreakdown";
import { TransactionType, transactionKey } from "../utils/constants";

const EXPENSE_COLORS = ["#ff6361", "#bc5090", "#58508d", "#ffa600", "#003f5c", "#7a5195", "#ef5675", "#2f4b7c", "#665191", "#d45087"];
const INCOME_COLORS = ["#73AC61", "#A6C760", "#E2E062", "#297159", "#15534C", "#4CAF50", "#81C784"];

const COLORS = {
    pageBg: "#f8f8f8",
    darkText: "#333",
    secondaryText: "#666",
};

const MONTH_NAMES = {
    1: "ינואר", 2: "פברואר", 3: "מרץ", 4: "אפריל", 5: "מאי", 6: "יוני",
    7: "יולי", 8: "אוגוסט", 9: "ספטמבר", 10: "אוקטובר", 11: "נובמבר", 12: "דצמבר",
};

const getPageStyle = (isMobile) => ({
    background: COLORS.pageBg,
    minHeight: "100vh",
    padding: isMobile ? "8px 0" : "24px 0",
    overflowX: "hidden",
});

const getContainerStyle = (isMobile) => ({
    maxWidth: isMobile ? "100%" : "1100px",
    margin: "0 auto",
    padding: isMobile ? "0 12px" : "0 24px",
    overflowX: "hidden",
});

const getMonthTitleStyle = (isMobile) => ({
    textAlign: "center",
    fontSize: isMobile ? "18px" : "22px",
    fontWeight: "bold",
    color: COLORS.darkText,
    marginBottom: "8px",
    direction: "rtl",
});

const navContainerStyle = {
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
};

export default function MonthSummaryMain({ isMobile = false }) {
    const { selectedYear, selectedMonthId, setSelectedMonthId, setSelectedYear } = useContext(AppContext);
    const touchStartRef = useRef(null);
    const { normalizedIds, normalizedAmounts, hasNormalization } = useNormalization();

    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [incomeCategories, setIncomeCategories] = useState([]);
    const [anomalies, setAnomalies] = useState([]);
    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [recurringDescriptions, setRecurringDescriptions] = useState(new Set());
    const [expenseCategoryTransactions, setExpenseCategoryTransactions] = useState({});
    const [incomeCategoryTransactions, setIncomeCategoryTransactions] = useState({});

    useEffect(() => {
        loadData();
    }, [selectedYear, selectedMonthId]);

    const loadData = async () => {
        setLoading(true);

        try {
            const params = {
                year: selectedYear,
                month: selectedMonthId,
            };

            const [summaryRes, categoriesRes, transactionsRes, anomaliesRes, recurringExpensesRes] = await Promise.all([
                Api.expenseVsIncomeByTime(params),
                Api.getMonthCategories(params),
                Api.getMonthTransactions(params),
                Api.getAnomalies(params),
                Api.getRecurringExpenses(params),
            ]);

            const summaryData = summaryRes.response?.[0] || { income: 0, expense: 0, balance: 0 };
            setSummary(summaryData);

            const {
                currentExpenseCategories = [],
                variableExpenseCategories = [],
                incomeCategories: incomeCats = [],
            } = categoriesRes.response || {};

            const allExpenseCategories = [...currentExpenseCategories, ...variableExpenseCategories]
                .sort((a, b) => b.amount - a.amount)
                .map((cat, idx) => ({
                    ...cat,
                    color: EXPENSE_COLORS[idx % EXPENSE_COLORS.length],
                    percentage: 0,
                }));

            const expTotal = allExpenseCategories.reduce((sum, c) => sum + c.amount, 0);
            allExpenseCategories.forEach((cat) => {
                cat.percentage = expTotal > 0 ? Math.round((cat.amount / expTotal) * 1000) / 10 : 0;
            });

            const enrichedIncomeCats = incomeCats
                .sort((a, b) => b.amount - a.amount)
                .map((cat, idx) => ({
                    ...cat,
                    color: INCOME_COLORS[idx % INCOME_COLORS.length],
                    percentage: 0,
                }));

            const incTotal = enrichedIncomeCats.reduce((sum, c) => sum + c.amount, 0);
            enrichedIncomeCats.forEach((cat) => {
                cat.percentage = incTotal > 0 ? Math.round((cat.amount / incTotal) * 1000) / 10 : 0;
            });

            setExpenseCategories(allExpenseCategories);
            setIncomeCategories(enrichedIncomeCats);

            const { expenseTransactions = [], incomeTransactions = [] } = transactionsRes.response || {};

            const mapTx = (tx) => ({
                id: tx.id || tx.transactionId,
                description: tx.description,
                amount: Math.abs(tx.amount),
                date: tx.time ? new Date(tx.time).toISOString().split("T")[0] : "",
                accountType: tx.type === "bank" ? "bank" : "creditCard",
                categoryId: tx.categoryId,
                type: tx.type,
                isAnomaly: tx.isAnomaly || false,
                anomalyReason: tx.anomalyReason || "",
            });

            const sortAnomaliesFirst = (a, b) => {
                if (a.isAnomaly && !b.isAnomaly) return -1;
                if (!a.isAnomaly && b.isAnomaly) return 1;
                return Math.abs(b.amount) - Math.abs(a.amount);
            };

            const expenseTxByCategory = {};
            allExpenseCategories.forEach((cat) => {
                expenseTxByCategory[cat.categoryId] = expenseTransactions
                    .filter((tx) => tx.categoryId === cat.categoryId)
                    .sort(sortAnomaliesFirst)
                    .map(mapTx);
            });

            const incomeTxByCategory = {};
            enrichedIncomeCats.forEach((cat) => {
                incomeTxByCategory[cat.categoryId] = incomeTransactions
                    .filter((tx) => tx.categoryId === cat.categoryId)
                    .sort(sortAnomaliesFirst)
                    .map(mapTx);
            });

            setExpenseCategoryTransactions(expenseTxByCategory);
            setIncomeCategoryTransactions(incomeTxByCategory);

            const { anomalyGroups = [], recurringDescriptions: recDescs = [] } = anomaliesRes.response || {};
            setAnomalies(anomalyGroups);
            setRecurringDescriptions(new Set(recDescs.map((d) => d.toLowerCase())));

            setRecurringExpenses(recurringExpensesRes.response || []);
        } catch (err) {
            console.error("Failed to load month summary data:", err);
        }

        setLoading(false);
    };

    const handleTouchStart = useCallback((e) => {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!touchStartRef.current) return;
        const dx = touchStartRef.current.x - e.touches[0].clientX;
        const dy = touchStartRef.current.y - e.touches[0].clientY;
        if (Math.abs(dx) < Math.abs(dy)) return;
        if (Math.abs(dx) < 50) return;

        if (dx > 0) {
            const newMonth = Number(selectedMonthId) - 1;
            if (newMonth < 1) { setSelectedYear(Number(selectedYear) - 1); setSelectedMonthId(12); }
            else setSelectedMonthId(newMonth);
        } else {
            const newMonth = Number(selectedMonthId) + 1;
            if (newMonth > 12) { setSelectedYear(Number(selectedYear) + 1); setSelectedMonthId(1); }
            else setSelectedMonthId(newMonth);
        }
        touchStartRef.current = null;
    }, [selectedMonthId, selectedYear, setSelectedMonthId, setSelectedYear]);

    const handleToggleRecurring = useCallback(async (tx) => {
        const descLower = tx.description.toLowerCase();
        const isCurrentlyRecurring = recurringDescriptions.has(descLower);
        try {
            if (isCurrentlyRecurring) {
                await Api.deleteRecurringTransaction({ description: tx.description });
                setRecurringDescriptions((prev) => {
                    const next = new Set(prev);
                    next.delete(descLower);
                    return next;
                });
            } else {
                await Api.addRecurringTransaction({
                    description: tx.description,
                    categoryId: tx.categoryId,
                    name: tx.description,
                    type: tx.type === "CreditCard" ? "CREDITCARD" : "BANK",
                });
                setRecurringDescriptions((prev) => new Set([...prev, descLower]));
            }
        } catch (err) {
            console.error("Failed to toggle recurring:", err);
        }
    }, [recurringDescriptions]);

    const { adjustedIncome, adjustedExpense, adjustedBalance } = useMemo(() => {
        let incomeAdj = 0;
        let expenseAdj = 0;
        normalizedIds.forEach((id) => {
            const item = normalizedAmounts[id];
            if (item) {
                if (item.transactionType === TransactionType.INCOME) incomeAdj += item.amount;
                else expenseAdj += item.amount;
            }
        });
        return {
            adjustedIncome: summary.income - incomeAdj,
            adjustedExpense: summary.expense - expenseAdj,
            adjustedBalance: (summary.income - incomeAdj) - (summary.expense - expenseAdj),
        };
    }, [normalizedIds, normalizedAmounts, summary]);

    const adjustedExpenseCategories = useMemo(() => {
        if (!hasNormalization) return expenseCategories;
        const reductions = getNormalizedReductions(normalizedIds, normalizedAmounts, TransactionType.EXPENSE);
        return recalcCategories(expenseCategories, reductions);
    }, [hasNormalization, normalizedIds, normalizedAmounts, expenseCategories]);

    const adjustedIncomeCategories = useMemo(() => {
        if (!hasNormalization) return incomeCategories;
        const reductions = getNormalizedReductions(normalizedIds, normalizedAmounts, TransactionType.INCOME);
        return recalcCategories(incomeCategories, reductions);
    }, [hasNormalization, normalizedIds, normalizedAmounts, incomeCategories]);

    const MonthNavigator = isMobile ? MobileMonthNavigator : DesktopMonthNavigator;

    const swipeProps = isMobile ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove } : {};

    return (
        <BlockUi
            tag="div"
            blocking={loading}
            loader={<Loader active type={"ball-pulse"} color="#02a17c" />}
            message={"...טוען נתונים"}
        >
            <div style={getPageStyle(isMobile)} {...swipeProps}>
                <div style={navContainerStyle}>
                    <div style={{ width: isMobile ? "100%" : "70%" }}>
                        <MonthNavigator />
                    </div>
                </div>

                <div style={getContainerStyle(isMobile)}>
                    <SummaryBar
                        income={adjustedIncome}
                        expense={adjustedExpense}
                        balance={adjustedBalance}
                        originalIncome={summary.income}
                        originalExpense={summary.expense}
                        originalBalance={summary.balance}
                        isMobile={isMobile}
                    />

                    {/* {anomalies.length > 0 && (
                        <AnomalyPanel
                            anomalyGroups={anomalies}
                            onToggleRecurring={handleToggleRecurring}
                            recurringDescriptions={recurringDescriptions}
                            isMobile={isMobile}
                        />
                    )} */}

                    <RecurringExpenses items={recurringExpenses} isMobile={isMobile} />

                    {adjustedExpenseCategories.length > 0 && (
                        <CategoryBreakdown
                            title="הוצאות לפי קטגוריה"
                            categories={adjustedExpenseCategories}
                            transactions={expenseCategoryTransactions}
                            type={TransactionType.EXPENSE}
                            onToggleRecurring={handleToggleRecurring}
                            recurringDescriptions={recurringDescriptions}
                            isMobile={isMobile}
                        />
                    )}

                    {adjustedIncomeCategories.length > 0 && (
                        <CategoryBreakdown
                            title="הכנסות לפי קטגוריה"
                            categories={adjustedIncomeCategories}
                            transactions={incomeCategoryTransactions}
                            type={TransactionType.INCOME}
                            onToggleRecurring={handleToggleRecurring}
                            recurringDescriptions={recurringDescriptions}
                            isMobile={isMobile}
                        />
                    )}
                </div>
            </div>
        </BlockUi>
    );
}

function recalcCategories(categories, reductions) {
    const adjusted = categories.map((cat) => {
        const reduction = reductions[cat.categoryId] || 0;
        if (reduction > 0) return { ...cat, amount: Math.max(0, cat.amount - reduction) };
        return cat;
    });
    const newTotal = adjusted.reduce((sum, c) => sum + c.amount, 0);
    return adjusted.map((cat) => ({
        ...cat,
        percentage: newTotal > 0 ? Math.round((cat.amount / newTotal) * 1000) / 10 : 0,
    }));
}

function getNormalizedReductions(normalizedIds, normalizedAmounts, transactionType) {
    const reductions = {};
    normalizedIds.forEach((key) => {
        const item = normalizedAmounts[key];
        if (!item || item.transactionType !== transactionType || !item.categoryId) return;
        reductions[item.categoryId] = (reductions[item.categoryId] || 0) + item.amount;
    });
    return reductions;
}